import { Link } from 'react-router-dom';
import type { GrahaBody, VargaChart } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { KUNDLI_GUIDE_ANCHOR } from './ChartStyleGuide';
import {
  ExplainChip,
  HouseHitArea,
  KundliPopover,
  useKundliTouch,
  type HouseTapInfo,
} from './KundliTouch';

/**
 * North Indian (diamond) chart style. Houses are fixed positions; sign
 * numbers rotate with the lagna. Requires a lagna, so it renders only for
 * exact-time charts (window modes use the stability views instead).
 *
 * Every compartment is tappable: a transparent polygon over each house opens
 * an anchored popover naming the bhava, its rashi and the grahas sitting
 * there. The "?" chip turns on a pulsing dot per compartment as an invitation.
 */

const PLANET_ABBR: Record<string, string> = {
  sun: 'Su',
  moon: 'Mo',
  mars: 'Ma',
  mercury: 'Me',
  jupiter: 'Ju',
  venus: 'Ve',
  saturn: 'Sa',
  rahu: 'Ra',
  ketu: 'Ke',
  uranus: 'Ur',
  neptune: 'Ne',
  pluto: 'Pl',
  lagna: 'La',
};

/** House label anchor points for a 300×300 square, houses 1..12. */
const HOUSE_POS: [number, number][] = [
  [150, 78], // 1
  [72, 40], // 2
  [40, 78], // 3
  [78, 150], // 4
  [40, 222], // 5
  [72, 260], // 6
  [150, 222], // 7
  [228, 260], // 8
  [260, 222], // 9
  [222, 150], // 10
  [260, 78], // 11
  [228, 40], // 12
];

/**
 * Sign-number anchors. The four diamond houses (1, 4, 7, 10) put their number
 * at the centre-facing tip of the compartment, which is the printed-kundli
 * convention — but they must be pushed far enough out not to collide with
 * each other in the middle of the chart.
 */
const SIGN_NUM_POS: [number, number][] = [
  [150, 124], // 1
  [75, 14], // 2
  [14, 75], // 3
  [124, 150], // 4
  [14, 225], // 5
  [75, 288], // 6
  [150, 180], // 7
  [225, 288], // 8
  [288, 225], // 9
  [180, 150], // 10
  [288, 75], // 11
  [225, 14], // 12
];

/**
 * Bhava-number anchors, houses 1..12 — the OUTER corner of each compartment,
 * which is exactly where the rotating sign numbers never sit. Bhava positions
 * are fixed in the North style, so these labels never move; the sign numbers
 * rotating past them is the whole story of the chart, and the founder's rule
 * is that the Western wheel's visible house numbers get an Indian equivalent.
 */
const BHAVA_NUM_POS: [number, number][] = [
  [150, 16], // 1 — top vertex of the top kite
  [20, 12], // 2 — along the top edge, corner side
  [12, 26], // 3 — along the left edge, corner side
  [16, 153], // 4 — left vertex of the left kite
  [12, 280], // 5
  [20, 292], // 6
  [150, 290], // 7 — bottom vertex of the bottom kite
  [280, 292], // 8
  [288, 280], // 9
  [284, 153], // 10 — right vertex of the right kite
  [288, 26], // 11
  [280, 12], // 12
];

/**
 * The exact outline of each house compartment, houses 1..12 — together they
 * tile the whole 300×300 diamond layout, so every point of the drawing
 * belongs to exactly one tap target.
 */
const HOUSE_HIT_POINTS: string[] = [
  '150,2 224,76 150,150 76,76', // 1 — top kite
  '2,2 150,2 76,76', // 2
  '2,2 76,76 2,150', // 3
  '2,150 76,76 150,150 76,224', // 4 — left kite
  '2,150 76,224 2,298', // 5
  '2,298 76,224 150,298', // 6
  '150,298 76,224 150,150 224,224', // 7 — bottom kite
  '150,298 224,224 298,298', // 8
  '298,298 224,224 298,150', // 9
  '298,150 224,224 150,150 224,76', // 10 — right kite
  '298,150 224,76 298,2', // 11
  '298,2 224,76 150,2', // 12
];

export function NorthIndianChart({
  varga,
  title,
}: {
  varga: VargaChart;
  title?: string;
}) {
  const { t } = useI18n();
  const touch = useKundliTouch();
  const lagna = varga.placements.find((p) => p.body === 'lagna');
  if (!lagna) return null;
  const lagnaSign = lagna.sign;

  const byHouse = new Map<number, GrahaBody[]>();
  for (const p of varga.placements) {
    if (p.body === 'lagna') continue;
    if (['uranus', 'neptune', 'pluto'].includes(p.body)) continue;
    const house = ((p.sign - lagnaSign + 12) % 12) + 1;
    const list = byHouse.get(house) ?? [];
    list.push(p.body as GrahaBody);
    byHouse.set(house, list);
  }

  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="spread" style={{ marginBottom: 8 }}>
        {title ? <div className="h3">{title}</div> : <span aria-hidden />}
        <ExplainChip on={touch.explain} onToggle={touch.toggleExplain} />
      </div>
      <svg viewBox="0 0 300 300" style={{ width: '100%', height: 'auto' }}>
        <rect x="2" y="2" width="296" height="296" fill="none" stroke="var(--border-strong)" strokeWidth="1.5" rx="6" />
        <line x1="2" y1="2" x2="298" y2="298" stroke="var(--border-strong)" strokeWidth="1" />
        <line x1="298" y1="2" x2="2" y2="298" stroke="var(--border-strong)" strokeWidth="1" />
        <path d="M 150 2 L 298 150 L 150 298 L 2 150 Z" fill="none" stroke="var(--border-strong)" strokeWidth="1" />

        {HOUSE_POS.map(([x, y], i) => {
          const house = i + 1;
          const planets = byHouse.get(house) ?? [];
          return (
            <g key={house}>
              <text
                x={SIGN_NUM_POS[i][0]}
                y={SIGN_NUM_POS[i][1] + 4}
                textAnchor="middle"
                fontSize="12"
                fill="var(--text-faint)"
              >
                {((lagnaSign + i) % 12) + 1}
              </text>
              {planets.map((body, j) => (
                <text
                  key={body}
                  x={x + (j % 2 === 0 ? -14 : 14) * (planets.length > 1 ? 1 : 0)}
                  y={y + Math.floor(j / 2) * 14 - (planets.length > 2 ? 8 : 0)}
                  textAnchor="middle"
                  fontSize="13"
                  fontWeight="600"
                  // --gold-ink, not --gold: the fill value is only legible on
                  // a dark page. The ink variant tracks the active theme.
                  fill={house === 1 ? 'var(--gold-ink)' : 'var(--text)'}
                >
                  {PLANET_ABBR[body]}
                </text>
              ))}
            </g>
          );
        })}

        {/* Fixed bhava numbers at each compartment's outer corner. Smaller
            and fainter than the rotating rashi numbers, and explained by the
            one-line legend under the chart — matching the Western wheel,
            which has always denoted its houses visibly. */}
        {BHAVA_NUM_POS.map(([x, y], i) => (
          <text
            key={`bhava-${i + 1}`}
            x={x}
            y={y}
            textAnchor="middle"
            fontSize="8"
            fill="var(--text-faint)"
          >
            {i + 1}
          </text>
        ))}

        {/* Hit areas last, so they sit on top of every painted layer. */}
        {HOUSE_HIT_POINTS.map((points, i) => {
          const house = i + 1;
          const info: HouseTapInfo = {
            bhava: house,
            sign: (lagnaSign + i) % 12,
            grahas: byHouse.get(house) ?? [],
          };
          return (
            <HouseHitArea
              key={house}
              shape={{ kind: 'polygon', points }}
              info={info}
              explain={touch.explain}
              active={touch.active?.info.bhava === house}
              onTap={touch.tap}
            />
          );
        })}
      </svg>
      <div className="tiny faint center" style={{ marginTop: 6 }}>
        {varga.varga} — {varga.description}
      </div>
      <div className="tiny faint center" style={{ marginTop: 4 }}>
        {t('kundli.legend.north')}
      </div>
      <div className="center" style={{ marginTop: 6 }}>
        <Link to={`/charts#${KUNDLI_GUIDE_ANCHOR}`} className="tiny accent">
          {t('kundli.howToRead')} →
        </Link>
      </div>
      {touch.active && (
        <KundliPopover info={touch.active.info} anchor={touch.active.anchor} onClose={touch.close} />
      )}
    </div>
  );
}
